import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, AlertCircle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const StudentLeaveReport = () => {
  const [leaves, setLeaves] = useState([]);
  const [summary, setSummary] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/leave/student-leaves');
      const data = Array.isArray(response.data) ? response.data : [];
      setLeaves(data);
      const approved = data.filter(l => l.status === 'Approved').length;
      const pending = data.filter(l => l.status === 'Pending').length;
      const rejected = data.filter(l => l.status === 'Rejected').length;
      setSummary({ total: data.length, approved, pending, rejected });
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load leave data. Please try again.');
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Student Leave Report</CardTitle>
          <Button onClick={fetchReport} disabled={loading}>
            <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchReport} className="ml-auto">Retry</Button>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Leaves</p>
              <p className="text-2xl font-bold text-blue-600">{summary.total}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">{summary.approved}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{summary.pending}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{summary.rejected}</p>
            </div>
          </div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Student Name</th>
                <th className="text-left p-3">Leave Type</th>
                <th className="text-left p-3">From</th>
                <th className="text-left p-3">To</th>
                <th className="text-left p-3">Days</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center p-8 text-gray-500">Loading...</td></tr>
              ) : leaves.length === 0 ? (
                <tr><td colSpan="6" className="text-center p-8 text-gray-500">No leave records found</td></tr>
              ) : (
                leaves.map((leave, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{leave.student_name}</td>
                    <td className="p-3">{leave.leave_type}</td>
                    <td className="p-3">{leave.from_date}</td>
                    <td className="p-3">{leave.to_date}</td>
                    <td className="p-3">{leave.number_of_days}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        leave.status === 'Approved' ? 'bg-green-100 text-green-800' : 
                        leave.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {leave.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentLeaveReport;
